<template>
  <div v-if="!!phaseData">
    <div v-if="predominantSymptom === 'AXIAL'">
      <field-label label="Sintoma predominante" class="mb-4" titleColorClass="grey--text text--darken-2">
        <span class="text-h6">ASAS Axial</span>
      </field-label>
      <field-label
        required
        label="O paciente tem elementos de espondiloartrite?"
        titleClass="text-h6"
        titleColorClass="grey--text text--darken-2"
      >
        <div class="mt-4">
          <radio-options
            v-for="element in axialAsasEspondiloartritesElementOptions"
            :key="element.key"
            clearable
            :label="element.name"
            v-model="phaseData[element.key]"
            row
            :options="defaultOptions"
            required
            :disabled="element.disabled"
            :subtitle="getSubtitle(element.disabled)"
          />
        </div>
        <template #label-extra>
          <info-btn
            size="medium"
            class="ml-4"
            @click="
              showImage(
                publicStorageUrl + '/disease-score-infographics/ASAS-axial.png',
                'Veja abaixo a tabela de cálculo para dar os resultados',
                'Asas Axial',
              )
            "
          />
        </template>
      </field-label>
      <field-label
        label="As informações abaixo foram respondidas em sintomas. Caso queira alterar um deste itens, retorne à aba sintomas."
        titleClass="text-h6"
        titleColorClass="grey--text text--darken-2"
      >
        <div class="mt-4">
          <div v-for="symptom in axialAsasSymptomOptions" :key="symptom.key">
            <span class="font-weight-bold">{{ symptom.name }}:</span
            ><span class="ml-2">{{ symptomsPhaseData.symptoms[symptom.key].value ? 'Sim' : 'Não' }}</span>
          </div>
        </div>
      </field-label>
      <field-label
        label="O paciente tem dor lombar inflamatória?"
        titleClass="text-h6"
        titleColorClass="grey--text text--darken-2"
        required
        class="mt-4"
      >
        <div>
          <span class="font-weight-bold">Resposta:</span>
          <span class="font-weight-bold ml-2 text-h6" :class="inflammatoryPain ? 'red--text' : 'green--text'">{{
            inflammatoryPain ? 'SIM' : 'NÃO'
          }}</span>
        </div>
        <div class="mt-4">
          <radio-options
            v-for="pain in painOptions"
            :key="pain.key"
            clearable
            :label="pain.name"
            v-model="phaseData[pain.key]"
            row
            :options="defaultOptions"
            required
          />
        </div>
      </field-label>
      <field-label
        label="O paciente tem sacroileíte em exame de imagem?"
        titleClass="text-h6"
        titleColorClass="grey--text text--darken-2"
        required
        class="mt-4"
      >
        <radio-options
          class="mt-4"
          clearable
          v-model="phaseData['possui_sacroileite_exame_imagem']"
          :options="imageExamOptions"
        />
      </field-label>
    </div>
    <div v-else-if="predominantSymptom === 'PERIFERICO'">
      <field-label label="Sintoma predominante" class="mb-4" titleColorClass="grey--text text--darken-2">
        <span class="text-h6">ASAS Periférico</span>
      </field-label>
      <field-label label="Manifestações periféricas" titleClass="text-h6" titleColorClass="grey--text text--darken-2">
        <template #label-extra>
          <info-btn
            size="medium"
            class="ml-4"
            @click="
              showImage(
                publicStorageUrl + '/disease-score-infographics/asas-periferico.pbm',
                'Veja abaixo a tabela de cálculo para dar os resultados',
                'Asas Periférico',
              )
            "
          />
        </template>
        <field-label
          class="mt-4"
          label="As informações abaixo foram respondidas em sintomas. Caso queira alterar um deste itens, retorne à aba sintomas."
        >
          <div>
            <div v-for="symptom in perifericAsasSymptomOptions1" :key="symptom.key">
              <span class="font-weight-bold">{{ symptom.name }}:</span
              ><span class="ml-2">{{ symptomsPhaseData.symptoms[symptom.key].value ? 'Sim' : 'Não' }}</span>
            </div>
          </div>
        </field-label>
        <field-label
          titleClass="text-h6"
          titleColorClass="grey--text text--darken-2"
          label="Marque, por favor, as articulações acometidas"
          class="mt-4"
          v-if="symptomsPhaseData.symptoms.artrite_artralgia.value"
        >
          <div class="d-flex" :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }">
            <arthritis-body-model
              v-model="phaseData['articulacoes_acometidas_artrite']"
              :height="$vuetify.breakpoint.smAndDown ? '500px' : '600px'"
              class="mt-4 d-flex justify-center"
              :style="{
                'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : '50%',
                order: $vuetify.breakpoint.smAndDown ? 3 : 'auto',
              }"
              width="100%"
            />
            <v-divider
              :vertical="$vuetify.breakpoint.mdAndUp"
              :class="{
                'ml-auto': $vuetify.breakpoint.mdAndUp,
                'my-4': $vuetify.breakpoint.smAndDown,
              }"
              :style="{
                order: $vuetify.breakpoint.smAndDown ? 2 : 'auto',
              }"
            />
            <div
              class="d-flex flex-column"
              :class="{ 'pl-12': $vuetify.breakpoint.mdAndUp }"
              :style="{
                'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : '50%',
                order: $vuetify.breakpoint.smAndDown ? 1 : 'auto',
              }"
            >
              <span class="font-weight-bold text-h6 mb-2">Articulações acometidas</span>

              <span v-if="articulacoesAcometidasArtrite.length === 0">Sem lesões</span>

              <div v-if="computedArticulacoesDolorosas > 0">
                <span class="font-weight-bold">Articulações dolorosas:</span>
                <span class="big-number--negative ml-2">{{ computedArticulacoesDolorosas }}</span>
              </div>
              <div v-if="computedArticulacoesInchadas > 0">
                <span class="font-weight-bold">Articulações inchadas:</span>
                <span class="big-number--negative ml-2">{{ computedArticulacoesInchadas }}</span>
              </div>
              <div
                v-for="articulacao in articulacoesAcometidasArtrite"
                :key="articulacao.key"
                class="d-flex flex-column mt-2"
              >
                <span class="font-weight-bold"> {{ articulacao.value.label }}</span>
                <span class="ml-8" v-if="articulacao.value.dorSelected">Dolorosa</span>
                <span class="ml-8" v-if="articulacao.value.inchSelected">Inchada</span>
              </div>
            </div>
          </div>
        </field-label>
        <field-label
          titleClass="text-h6"
          titleColorClass="grey--text text--darken-2"
          label="Marque, por favor, as áreas acometidas"
          class="mt-4"
          v-if="symptomsPhaseData.symptoms.entesite.value"
        >
          <div class="d-flex" :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }">
            <entesitis-body-model
              v-model="phaseData['areas_acometidas_entesite']"
              height="600px"
              class="mt-4 d-flex justify-center"
              :style="{
                'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : '50%',
                order: $vuetify.breakpoint.smAndDown ? 3 : 'auto',
              }"
              width="100%"
            />
            <v-divider
              :vertical="$vuetify.breakpoint.mdAndUp"
              :class="{
                'ml-auto': $vuetify.breakpoint.mdAndUp,
                'my-4': $vuetify.breakpoint.smAndDown,
              }"
              :style="{
                order: $vuetify.breakpoint.smAndDown ? 2 : 'auto',
              }"
            />
            <div
              class="d-flex flex-column"
              :class="{ 'pl-12': $vuetify.breakpoint.mdAndUp }"
              :style="{
                'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : '50%',
                order: $vuetify.breakpoint.smAndDown ? 1 : 'auto',
              }"
            >
              <span class="font-weight-bold text-h6 mb-2">Ênteses acometidas</span>

              <span v-if="areasAcometidasEntesite.length === 0">Sem lesões</span>

              <div v-if="areasAcometidasEntesite.length > 0">
                <span class="font-weight-bold">Total:</span>
                <span class="big-number--negative ml-2">{{ areasAcometidasEntesite.length }}</span>
              </div>

              <div v-for="area in areasAcometidasEntesite" :key="area.key" class="d-flex flex-column mt-2">
                <span class="font-weight-bold"> {{ area.value.label }}</span>
              </div>
            </div>
          </div>
        </field-label>
        <field-label
          titleClass="text-h6"
          titleColorClass="grey--text text--darken-2"
          label="Marque, por favor, os dedos acometidos"
          class="mt-4"
          v-if="symptomsPhaseData.symptoms.dactilite.value"
        >
          <div class="d-flex" :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }">
            <dactilitis-body-model
              v-model="phaseData['areas_acometidas_dactilite']"
              :height="$vuetify.breakpoint.smAndDown ? '500px' : '600px'"
              class="mt-4 d-flex justify-center"
              :style="{
                'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : '50%',
                order: $vuetify.breakpoint.smAndDown ? 3 : 'auto',
              }"
              width="100%"
            />
            <v-divider
              :vertical="$vuetify.breakpoint.mdAndUp"
              :class="{
                'ml-auto': $vuetify.breakpoint.mdAndUp,
                'my-4': $vuetify.breakpoint.smAndDown,
              }"
              :style="{
                order: $vuetify.breakpoint.smAndDown ? 2 : 'auto',
              }"
            />
            <div
              class="d-flex flex-column"
              :class="{ 'pl-12': $vuetify.breakpoint.mdAndUp }"
              :style="{
                'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : '50%',
                order: $vuetify.breakpoint.smAndDown ? 1 : 'auto',
              }"
            >
              <span class="font-weight-bold text-h6 mb-2">Dáctilos acometidos</span>

              <span v-if="areasAcometidasDactilite.length === 0">Sem lesões</span>

              <div v-if="areasAcometidasDactilite.length > 0">
                <span class="font-weight-bold">Total:</span>
                <span class="big-number--negative ml-2">{{ areasAcometidasDactilite.length }}</span>
              </div>

              <div v-for="area in areasAcometidasDactilite" :key="area.key" class="d-flex flex-column mt-2">
                <span class="font-weight-bold"> {{ area.value.label }}</span>
              </div>
            </div>
          </div>
        </field-label>
        <field-label
          class="mt-4"
          required
          label="Quais outros elementos de espondiloartrite o paciente tem?"
          titleClass="text-h6"
          titleColorClass="grey--text text--darken-2"
        >
          <div class="mt-4">
            <radio-options
              v-for="element in perifericAsasEspondiloartritesElementOptions"
              :key="element.key"
              clearable
              :label="element.name"
              v-model="phaseData[element.key]"
              row
              :options="defaultOptions"
              required
              :disabled="element.disabled"
              :subtitle="getSubtitle(element.disabled)"
            />
          </div>
        </field-label>
        <field-label
          class="mt-4"
          label="As informações abaixo foram respondidas em sintomas. Caso queira alterar um deste itens, retorne à aba sintomas."
        >
          <div>
            <div v-for="symptom in perifericAsasSymptomOptions2" :key="symptom.key">
              <span class="font-weight-bold">{{ symptom.name }}:</span
              ><span class="ml-2">{{ symptomsPhaseData.symptoms[symptom.key].value ? 'Sim' : 'Não' }}</span>
            </div>
          </div>
        </field-label>
      </field-label>
    </div>
    <div v-else>
      <h2>É necessário definir um sintoma predominante na aba Anamnese &gt; Sintomas</h2>
    </div>

    <div v-if="!!predominantSymptom">
      <field-label label="CASPAR" titleClass="text-h6" titleColorClass="grey--text text--darken-2" class="mt-4">
        <template #label-extra>
          <info-btn
            size="medium"
            class="ml-4"
            @click="
              showImage(
                publicStorageUrl + '/disease-score-infographics/CASPAR.jpeg',
                'Veja abaixo a tabela de cálculo para dar os resultados',
                'CASPAR',
              )
            "
          />
        </template>
        <div>
          <span class=""
            >Exame complementar que aumentará ainda mais a precisão do diagnóstico. Utilizar somente para pacientes com
            dor inflamatória articular</span
          >
        </div>
        <div class="mt-4">
          <radio-options
            v-for="option in casparOptions"
            v-show="option.key !== 'caspar_condicao_psoriase' || phaseData['caspar_psoriase']"
            :key="option.key"
            clearable
            :label="option.name"
            v-model="phaseData[option.key]"
            row
            :options="option.options || defaultOptions"
            required
            @input="checkCaspar(option.key, $event)"
            :disabled="option.disabled"
            :subtitle="getSubtitle(option.disabled)"
          />
        </div>
      </field-label>

      <field-label class="mt-8" label="Anotações">
        <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.notes" />
      </field-label>
    </div>

    <reference-image-dialog ref="referenceImageDialog" />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import { PUBLIC_STORAGE_URL } from '@/constants/global'
import FieldLabel from '@/components/FieldLabel.vue'
import ReferenceImageDialog from '../../ReferenceImageDialog.vue'
import InfoBtn from '@/components/InfoBtn.vue'
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import TextArea from '@/components/TextArea.vue'
import ArthritisBodyModel from '../ArthritisBodyModel.vue'
import EntesitisBodyModel from '../EntesitisBodyModel.vue'
import DactilitisBodyModel from '../DactilitisBodyModel.vue'

import {
  ESPONDILOARTRITES_ELEMENTS_OPTIONS,
  IMAGE_EXAM_OPTIONS,
} from '@/components/AttendanceForm/espondiloartrites/constants/asasPhase'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

import perifericAsasMixin from '@/components/AttendanceForm/espondiloartrites/mixins/espondiloartritesPerifericAsasMixin'

export default {
  mixins: [perifericAsasMixin],
  extends: BasePhase,
  components: {
    FieldLabel,
    ReferenceImageDialog,
    InfoBtn,
    RadioOptions,
    TextArea,
    ArthritisBodyModel,
    EntesitisBodyModel,
    DactilitisBodyModel,
  },
  computed: {
    ...attendanceGettersMap(['getPhaseDataByKey']),

    inflammatoryPain() {
      return this.painOptions.filter((o) => this.phaseData[o.key]).length >= 4
    },
    symptomsPhaseData() {
      return this.getPhaseDataByKey('sintomas')
    },
    predominantSymptom() {
      return this.symptomsPhaseData?.predominantSymptom
    },

    axialAsasEspondiloartritesElementOptions() {
      return ESPONDILOARTRITES_ELEMENTS_OPTIONS.AXIAL
    },
    perifericAsasEspondiloartritesElementOptions() {
      return ESPONDILOARTRITES_ELEMENTS_OPTIONS.PERIFERICO
    },
  },
  data() {
    return {
      publicStorageUrl: PUBLIC_STORAGE_URL,
      defaultOptions: [
        { text: 'Sim', value: true },
        { text: 'Não', value: false },
      ],

      imageExamOptions: IMAGE_EXAM_OPTIONS,

      axialAsasSymptomOptions: [
        { key: 'artrite_artralgia', name: 'Artrite/artralgia' },
        { key: 'entesite', name: 'Entesite' },
        { key: 'uveite', name: 'Uveite' },
        { key: 'dactilite', name: 'Dactilite' },
        { key: 'psoriase', name: 'Psoriase' },
        { key: 'alteracao_habito_intestinal', name: 'Doença de Crohn' },
      ],
      perifericAsasSymptomOptions1: [
        { key: 'artrite_artralgia', name: 'Artrite/artralgia' },
        { key: 'entesite', name: 'Entesite' },
        { key: 'dactilite', name: 'Dactilite' },
      ],
      perifericAsasSymptomOptions2: [
        { key: 'uveite', name: 'Uveite' },
        { key: 'psoriase', name: 'Psoriase' },
        { key: 'alteracao_habito_intestinal', name: 'Alteração de hábito instestinal' },
      ],

      painOptions: [
        { key: 'dor_lombar_melhora_com_exercicios', name: 'Dor melhora com exercícios?' },
        { key: 'dor_lombar_nao_melhora_com_repouso', name: 'Dor não melhora com repouso?' },
        { key: 'dor_lombar_piora_noite', name: 'Piora durante a noite?' },
        { key: 'dor_lombar_inicio_insidioso', name: 'Teve inicio insidioso?' },
        { key: 'dor_lombar_inicio_antes_40_anos', name: 'Iniciou antes dos 40 anos de idade?' },
      ],
      casparOptions: [
        { key: 'caspar_psoriase', name: 'Psoríase' },
        {
          key: 'caspar_condicao_psoriase',
          name: 'Indique a condição da psoríase',
          options: [
            { text: 'Atual', value: 'ATUAL' },
            { text: 'Prévia', value: 'PREVIA' },
            { text: 'História familiar', value: 'HISTORIA_FAMILIAR' },
          ],
        },
        { key: 'caspar_distrofia_ungueal', name: 'Distrofia ungueal' },
        { key: 'caspar_fator_reumatoide_negativo', name: 'Fator reumatoide negativo' },
        { key: 'caspar_dactilite', name: 'Histórico ou presença de dactilite', disabled: true },
        { key: 'caspar_evidencia_neoformacao_ossea', name: 'Rx com evidência de neoformação óssea' },
      ],
    }
  },
  methods: {
    postPhaseDataLoaded() {
      if (this.predominantSymptom !== this.phaseData.predominantSymptom) {
        this.phaseData = this.getDefaultData()
        this.phaseData.predominantSymptom = this.predominantSymptom
        this.phaseData.caspar_dactilite = this.symptomsPhaseData.symptoms.dactilite.value

        if (this.predominantSymptom === 'AXIAL') {
          this.phaseData.doenca_de_crohn_ou_retocolite_ulcerativa =
            this.symptomsPhaseData.symptoms.alteracao_habito_intestinal.value
        }
        if (this.predominantSymptom === 'PERIFERICO') {
          this.phaseData.doenca_inflamatoria_intestinal =
            this.symptomsPhaseData.symptoms.alteracao_habito_intestinal.value
        }
      }
    },
    getDefaultData() {
      const data = {
        ...this.buildEmptyCaspar(),
        predominantSymptom: null,
        articulacoes_acometidas_artrite: {},
        areas_acometidas_entesite: {},
        areas_acometidas_dactilite: {},
      }
      return data
    },
    showImage(imageSrc, description, title) {
      this.$refs.referenceImageDialog.openDialog(imageSrc, title, description)
    },
    checkCaspar(key, value) {
      if (key === 'caspar_psoriase') {
        this.phaseData['caspar_condicao_psoriase'] = null
      }
    },
    buildEmptyCaspar() {
      return this.casparOptions.reduce((acc, option) => ({ ...acc, [option.key]: null }), {})
    },
    getSubtitle(showSub) {
      if (!showSub) {
        return null
      }
      return 'Essa opção já foi preenchida em Anamnese - Sintomas'
    },
  },
}
</script>

<style scoped>
.big-number--negative {
  color: red;
  font-weight: medium;
  font-size: 1.5rem;
}
</style>
